import type { BlockGuide } from "./types";

export const guide: BlockGuide = {
  slug: "twitch",
  app: "Twitch",
  title: "How to block Twitch on iPhone — live is the whole problem",
  h1: "How to block Twitch",
  description:
    "Screen Time steps, why a live stream cannot be saved for later, and what to do about a habit built entirely on missing out.",
  targets: [
    "how to block twitch on iphone",
    "stop watching twitch",
    "twitch time limit",
    "quit watching streams",
  ],
  published: "2026-09-03",
  standfirst:
    "Everything else on your phone will still be there tomorrow. That is precisely what Twitch has removed, and it is why the sessions are so long.",
  takeaways: [
    "Live means it cannot be postponed, which converts every notification into a deadline rather than an invitation.",
    "Twitch sessions are long rather than frequent, so a daily limit is the right instrument here — unusually for this list.",
    "Turn off live notifications first. They are the mechanism, and they are the one thing you can remove without losing anything you wanted.",
  ],
  mechanic: [
    "Twitch's distinguishing feature is that its content is not storable. A video waits for you; a stream does not. Whatever is happening is happening now, the chat that makes it worth watching is happening now, and a person who leaves has not paused anything — they have missed it.",
    "That single property changes the shape of the habit. Other apps produce many short visits; Twitch produces a small number of very long ones, because the cost of leaving is not boredom but absence. And the notification that a streamer has gone live is not an invitation to watch something, it is the start of a window that will close, which is a far more effective message than any recommendation.",
    "The chat compounds it. Twitch is a social space as much as a video service, and the reason to be present is partly that other people are — which means the pull has the same shape as a room you are not in, rather than the shape of a feed you are not scrolling.",
  ],
  limitFails:
    "For once the failure is not that limits are the wrong instrument — a daily amount is broadly right for an app used in long sessions. The failure is the notification, which arrives before the limit is relevant and creates a sense of urgency that makes dismissing the limit feel reasonable: the stream is on now, the limit can be dealt with tomorrow. A limit fights the session; the notification fights the decision, and it gets there first.",
  instead: [
    "Turn off live notifications entirely — in Twitch's own settings, not just iOS. They are the deadline generator.",
    "Set a daily App Limit under Entertainment. This is one of the few apps where a time budget genuinely matches the behaviour.",
    "Follow fewer channels. Twenty follows means several deadlines a day; three means a couple a week.",
    "Watch VODs rather than live where the streamer posts them. It restores the ability to postpone, which is the thing the format removed.",
  ],
  verses: [
    {
      verse: {
        text: "Therefore watch carefully how you walk, not as unwise, but as wise, redeeming the time, because the days are evil.",
        ref: "Ephesians 5:15–16",
      },
      note: "Redeeming is buying back at a price, which is a fair description of what it takes to recover an evening from something that is happening now whether you are there or not. The passage assumes time is being lost by default and that keeping it requires deliberate action rather than good intentions.",
    },
    {
      verse: {
        text: "So teach us to count our days, that we may gain a heart of wisdom.",
        ref: "Psalm 90:12",
      },
      note: "A request for arithmetic from a psalm about how short life is — and the wisdom is described as the product of the counting rather than a separate thing. For an app whose sessions are measured in hours rather than minutes, the counting is the intervention: most people genuinely do not know the number until they look.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "Missing out is the product",
      id: "missing-out-is-the-product",
    },
    {
      type: "p",
      text: "Fear of missing out is usually a vague thing people invoke about social media. On Twitch it is literal and structural: the thing genuinely will not be there later, and the chat definitely will not.",
    },
    {
      type: "p",
      text: "Which means the useful intervention is upstream of the session. Once you are in, you are watching something that is ending, and asking yourself to leave early is asking for a loss.",
    },
    {
      type: "h2",
      text: "Notifications are deadlines",
      id: "notifications-are-deadlines",
    },
    {
      type: "p",
      text: "A notification saying somebody posted a video is an invitation. A notification saying somebody is live is a countdown. They look identical on a lock screen and they do completely different things to a person.",
    },
    {
      type: "note",
      text: "Turning off live notifications is the single highest-value change here, and unlike most of this page it costs you nothing you actually wanted.",
    },
    {
      type: "h2",
      text: "Long sessions, not many",
      id: "long-sessions-not-many",
    },
    {
      type: "p",
      text: "Most of this site argues that time limits are a poor instrument, because most phone damage is frequency. Twitch is the exception: the sessions are long, deliberate and few, which is exactly the shape a daily budget is designed for.",
    },
    {
      type: "p",
      text: "So set one, and then deal with the reason the evenings were empty in the first place — which is usually {the actual question}.",
      links: {
        "the actual question": "/prayers/prayer-for-loneliness",
      },
    },
  ],
  faq: [
    {
      q: "How do I block Twitch on my iPhone?",
      a: "Screen Time → App Limits → Entertainment, with Block at End of Limit and a passcode. Then turn off live notifications inside Twitch itself — those are what start the sessions.",
    },
    {
      q: "Why is Twitch harder to stop watching than YouTube?",
      a: "Because it cannot be postponed. A video waits; a stream does not, and neither does the chat. Leaving is not pausing, it is missing something.",
    },
    {
      q: "Do time limits work for Twitch?",
      a: "Better than for most apps, because the behaviour is a few long sessions rather than many short checks — which is the shape a daily budget is actually designed for.",
    },
  ],
  relatedApps: ["youtube", "netflix"],
  related: ["how-to-stop-scrolling-at-night", "dopamine-detox-christian"],
  relatedPrayers: ["prayer-for-loneliness", "prayer-before-bed"],
};
